/* ============================================================
   SMART-O® theme.js  — v3.0
   Vanilla JS only. No dependencies. Deferred load.
   ============================================================ */

(function () {
  'use strict';

  /* ---- UTILS ---- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  let cartUpdating = false; // Prevent overlapping cart quantity requests.


  /* ============================================================
     MOBILE NAV DRAWER
     ============================================================ */
  function initNavDrawer() {
    const drawer  = $('#nav-drawer');
    const overlay = $('#nav-drawer-overlay');
    const openBtn = $('#nav-drawer-open');
    const closeBtn = $('#nav-drawer-close');
    if (!drawer) return;

    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocused = null;

    const getFocusable = () => $$(focusableSelector, drawer).filter(el => el.offsetParent !== null || el === closeBtn);

    const open = () => {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      const focusables = getFocusable();
      (closeBtn || focusables[0])?.focus({ preventScroll: true });
    };

    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      (lastFocused || openBtn)?.focus({ preventScroll: true });
    };

    on(openBtn, 'click', open);
    on(closeBtn, 'click', close);
    on(overlay, 'click', close);
    on(document, 'keydown', e => {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') return close();
      if (e.key !== 'Tab') return;
      const focusables = getFocusable();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* FAQ accordion: intentionally no JS. <details name="..."> (see faq-section.liquid)
     natively provides single-open-group behavior. A prior version of this function
     manually toggled aria-expanded/hidden in parallel with the native <details> open
     state, which could desync the two — removed rather than patched. */

  /* ============================================================
     PRODUCT GALLERY
     ============================================================ */
  function initProductGallery() {
    const main   = $('#product-gallery-main');
    const thumbs = $$('.product-gallery__thumb');
    if (!main || !thumbs.length) return;

    thumbs.forEach(thumb => {
      on(thumb, 'click', () => {
        const src = thumb.dataset.src;
        const alt = thumb.dataset.alt || '';
        if (!src) return;
        const img = main.querySelector('img');
        if (img) {
          img.src = src;
          if (thumb.dataset.srcset) img.srcset = thumb.dataset.srcset;
          else img.removeAttribute('srcset');
          img.alt = alt;
        }
        thumbs.forEach(t => { t.classList.remove('is-active'); t.removeAttribute('aria-current'); });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ============================================================
     PRODUCT TABS
     ============================================================ */
  function initProductTabs() {
    $$('.product-tabs').forEach(tabs => {
      const btns   = $$('.product-tab-btn', tabs);
      const panels = $$('.product-tab-panel', tabs);
      if (!btns.length) return;

      function activateTab(index, focus = false) {
        const btn = btns[index];
        if (!btn) return;
        btns.forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); b.setAttribute('tabindex', '-1'); });
        panels.forEach(p => p.classList.remove('is-active'));
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
        if (panels[index]) panels[index].classList.add('is-active');
        if (focus) btn.focus();
      }

      btns.forEach((btn, i) => {
        btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
        on(btn, 'click', () => activateTab(i));
        on(btn, 'keydown', e => {
          const current = btns.indexOf(btn);
          let next = current;
          if (e.key === 'ArrowRight') next = (current + 1) % btns.length;
          else if (e.key === 'ArrowLeft') next = (current - 1 + btns.length) % btns.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = btns.length - 1;
          else return;
          e.preventDefault();
          activateTab(next, true);
        });
      });
    });
  }

  /* ============================================================
     VARIANT SELECTOR
     ============================================================ */
  function initVariantSelector() {
    const productJsonEl = $('[data-product-json]');
    const productData = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
    const form = $('#product-form');
    if (!productData || !form) return;

    const input = form.querySelector('[data-variant-input]') || form.querySelector('[name="id"]');
    const groups = $$('.variant-buttons', form);
    const priceEl = $('.product-info__price-current');
    const mobilePriceEl = $('.mobile-sticky-atc span');
    const addButtons = $$('[data-atc-btn], [data-mobile-atc]');
    const moneyFormat = window.theme?.moneyFormat || window.themeMoneyFormat || '${{ amount }}'; // Use Shopify money format injected by theme.liquid.

    function selectedOptions() {
      return groups.map(group => group.querySelector('.variant-btn.is-active')?.dataset.value).filter(Boolean);
    }

    function formatCents(cents) {
      const value = Number(cents || 0);
      const format = moneyFormat || '${{ amount }}';
      const placeholderMatch = format.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator)?|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/);
      const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
      const amount = value / 100;
      const withThousands = n => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withCommaDecimals = n => n.replace('.', ',');
      let replacement;

      switch (placeholder) {
        case 'amount_no_decimals':
          replacement = withThousands(Math.round(amount).toString());
          break;
        case 'amount_with_comma_separator':
          replacement = withCommaDecimals(withThousands(amount.toFixed(2)));
          break;
        case 'amount_no_decimals_with_comma_separator':
          replacement = withCommaDecimals(withThousands(Math.round(amount).toString()));
          break;
        case 'amount':
        default:
          replacement = withThousands(amount.toFixed(2));
          break;
      }

      return format.replace(/\{\{\s*amount(?:_no_decimals)?(?:_with_comma_separator)?\s*\}\}/, replacement);
    }

    function updateVariant() {
      const chosen = selectedOptions();
      const variant = productData.variants.find(v => v.options.every((opt, index) => opt === chosen[index]));
      if (!variant) return;

      if (input) input.value = variant.id;
      if (priceEl) priceEl.textContent = formatCents(variant.price);
      if (mobilePriceEl) mobilePriceEl.textContent = formatCents(variant.price);
      addButtons.forEach(btn => {
        const availableLabel = btn.dataset.defaultLabel || btn.textContent.trim() || 'Add SMART-O® plug to cart';
        const soldOutLabel = btn.dataset.soldOutLabel || 'Sold out';
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? availableLabel : soldOutLabel;
      });

      // Update URL with variant ID — enables sharing, back button and Google indexing
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variantId: variant.id }, '', url.toString());
      }
    }

    groups.forEach(group => {
      const btns = $$('.variant-btn', group);
      btns.forEach(btn => {
        on(btn, 'click', () => {
          btns.forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
          updateVariant();
        });
      });
    });

    updateVariant();
  }

  /* ============================================================
     QUANTITY CONTROLS
     ============================================================ */
  function initQtyControls() {
    $$('.qty-control').forEach(ctrl => {
      const input = $('input.qty-input', ctrl);
      const dec   = $('.qty-btn[data-action="dec"]', ctrl);
      const inc   = $('.qty-btn[data-action="inc"]', ctrl);
      if (!input) return;

      on(dec, 'click', () => {
        const v = parseInt(input.value, 10);
        if (v > 1) input.value = v - 1;
      });
      on(inc, 'click', () => {
        const v = parseInt(input.value, 10);
        input.value = v + 1;
      });
    });
  }

  /* ============================================================
     ADD TO CART — Async
     ============================================================ */
  function initAddToCart() {
    $$('.product-atc-form').forEach(form => {
      on(form, 'submit', async e => {
        e.preventDefault();
        const btn  = form.querySelector('[data-atc-btn]');
        const status = $('#atc-status');
        const data = new FormData(form);
        if (!btn) return;

        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        const originalText = btn.innerHTML;
        btn.innerHTML = '<span>Adding…</span>';
        if (status) status.textContent = 'Adding item to cart.';

        try {
          const res = await fetch(window.theme?.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: data
          });
          if (!res.ok) throw new Error('Cart error');
          btn.innerHTML = '<span>✓ Added</span>';
          btn.setAttribute('aria-busy', 'false');
          if (status) status.textContent = 'Item added to cart.';
          updateCartCount();
          setTimeout(() => { btn.innerHTML = originalText; btn.disabled = false; }, 2000);
        } catch {
          btn.innerHTML = '<span>Error — try again</span>';
          btn.disabled = false;
          btn.setAttribute('aria-busy', 'false');
          if (status) status.textContent = 'There was a problem adding this item. Try again.';
        }
      });
    });
  }

  async function updateCartCount() {
    try {
      const res  = await fetch(window.theme?.cartUrl?.endsWith('/cart') ? '/cart.js' : '/cart.js');
      const cart = await res.json();
      $$('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        if (el.classList.contains('header-icon__count')) {
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      });
    } catch { /* silent */ }
  }

  /* ============================================================
     CART QUANTITY UPDATE
     ============================================================ */
  function initCartUpdates() {
    $$('.cart-qty-input').forEach(input => {
      let timer;
      on(input, 'change', () => {
        clearTimeout(timer);
        timer = setTimeout(() => updateCartLine(input), 500);
      });
    });

    $$('[data-cart-qty-btn]').forEach(btn => {
      on(btn, 'click', () => updateCartLine(btn, parseInt(btn.dataset.quantity, 10)));
    });

    $$('[data-remove-line]').forEach(btn => {
      on(btn, 'click', () => updateCartLine(btn, 0));
    });
  }

  async function updateCartLine(el, qty) {
    if (cartUpdating) return;
    const line = el.dataset.line || el.closest('[data-line]')?.dataset.line;
    const quantity = qty !== undefined ? qty : parseInt(el.value, 10);
    const status = $('#cart-status');
    if (!line) return;

    cartUpdating = true;
    document.body.classList.add('cart-is-updating');

    try {
      const res = await fetch(window.theme?.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity })
      });
      if (res.ok) {
        if (status) status.textContent = quantity === 0 ? 'Item removed from cart.' : 'Cart updated.';
        window.location.reload();
      }
    } catch { /* silent */ }
    finally {
      cartUpdating = false;
      document.body.classList.remove('cart-is-updating');
    }
  }

  /* ============================================================
     LAZY IMAGES — IntersectionObserver
     ============================================================ */
  function initLazyImages() {
    if (!('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver((entries, o) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (img.dataset.src) img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.classList.add('is-loaded');
        o.unobserve(img);
      });
    }, { rootMargin: '200px' });
    $$('img[data-src], img[loading="lazy"]').forEach(img => obs.observe(img));
  }

  /* ============================================================
     SCROLL ANIMATIONS
     ============================================================ */
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) {
      $$('[data-animate]').forEach(el => el.classList.add('is-visible'));
      return;
    }
    const obs = new IntersectionObserver((entries, o) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        o.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    $$('[data-animate]').forEach(el => obs.observe(el));
  }

  /* ============================================================
     ANNOUNCEMENT BAR DISMISS
     ============================================================ */
  function initAnnouncementDismiss() {
    const bar = $('.announcement');
    const btn = $('.announcement__dismiss');
    if (!bar || !btn) return;
    on(btn, 'click', () => {
      bar.style.display = 'none';
      sessionStorage.setItem('smarto-announce-dismissed', '1');
    });
    if (sessionStorage.getItem('smarto-announce-dismissed')) bar.style.display = 'none';
  }

  /* ============================================================
     STICKY HEADER shrink on scroll
     ============================================================ */
  function initStickyHeader() {
    const header = $('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
    on(window, 'scroll', onScroll, { passive: true });
  }

  /* ============================================================
     NEWSLETTER FORM — Shopify customer subscribe
     ============================================================ */
  function initNewsletterForm() {
    $$('.newsletter__form').forEach(form => {
      on(form, 'submit', e => {
        // Let Shopify handle — just add loading state
        const btn = form.querySelector('button[type="submit"]');
        if (btn) btn.textContent = 'Subscribing…';
      });
    });
  }



  /* ============================================================
     VEHICLE FITMENT FINDER
     ============================================================ */
  function initVehicleFitmentFinder() {


    var year=document.getElementById('fitment-year'),
        make=document.getElementById('fitment-make'),
        model=document.getElementById('fitment-model'),
        cyl=document.getElementById('fitment-cylinders'),
        disp=document.getElementById('fitment-displacement'),
        query=document.getElementById('vehicle-fitment-query'),
        form=document.getElementById('vehicle-fitment-form');
    if(!form) return;
    if(!year || !make || !model || !query) return; // Guard optional fitment fields so one missing element cannot halt theme init.
    var models={
      'Toyota':['Camry','Corolla','RAV4','Highlander','Tacoma','Tundra','4Runner','Yaris','Prius','Land Cruiser'],
      'Ford':['F-150','Ranger','Explorer','Escape','Mustang','Transit','F-250','F-350','Bronco','Edge'],
      'Chevrolet':['Silverado','Colorado','Equinox','Tahoe','Malibu','Suburban','Traverse','Blazer','Trax'],
      'Honda':['Civic','Accord','CR-V','Pilot','Odyssey','HR-V','Passport','Ridgeline'],
      'Nissan':['Altima','Sentra','Rogue','Pathfinder','Frontier','Titan','Murano','Armada','Kicks'],
      'Hyundai':['Elantra','Sonata','Tucson','Santa Fe','Palisade','Kona','Ioniq'],
      'Kia':['Forte','Optima','K5','Sportage','Sorento','Telluride','Soul','Stinger'],
      'Mazda':['Mazda3','Mazda6','CX-3','CX-5','CX-9','CX-30','MX-5'],
      'Subaru':['Impreza','Legacy','Outback','Forester','Crosstrek','WRX','Ascent'],
      'Volkswagen':['Golf','Jetta','Passat','Tiguan','Atlas','Arteon','ID.4'],
      'BMW':['3 Series','5 Series','7 Series','X1','X3','X5','X7','M3','M5'],
      'Mercedes-Benz':['C-Class','E-Class','S-Class','GLA','GLC','GLE','GLS'],
      'Land Rover':['Range Rover','Range Rover Sport','Discovery','Defender','Velar','Evoque'],
      'Jeep':['Wrangler','Grand Cherokee','Cherokee','Compass','Gladiator','Renegade']
    };
    function unlock(el){ if(el){ el.disabled=false; } }
    function updateQuery(){
      var parts=['SMART-O oil drain plug', year.value, make.value, model.value, cyl && cyl.value, disp && disp.value].filter(Boolean);
      query.value=parts.join(' ');
      var result=document.getElementById('finder-result');
      var resultText=document.getElementById('finder-result-text');
      if(result && resultText){
        var labelParts=[year.value, make.value, model.value].filter(Boolean);
        if(labelParts.length >= 2){ resultText.textContent=labelParts.join(' '); result.hidden=false; }
        else { result.hidden=true; }
      }
    }
    year.addEventListener('change', function(){ unlock(make); updateQuery(); });
    make.addEventListener('change', function(){
      var list=models[make.value]||['Other'];
      model.innerHTML='<option value="">Select Model</option>'+list.map(function(m){return '<option>'+m+'</option>';}).join('');
      unlock(model); updateQuery();
    });
    model.addEventListener('change', function(){ unlock(cyl); unlock(disp); updateQuery(); });
    [cyl,disp].forEach(function(el){ if(el) el.addEventListener('change', updateQuery); });


  }

  /* ============================================================
     INIT ALL
     ============================================================ */
  function init() {
    initNavDrawer();
    initProductGallery();
    initProductTabs();
    initVehicleFitmentFinder();
    initVariantSelector();
    initQtyControls();
    initAddToCart();
    initCartUpdates();
    initLazyImages();
    initScrollAnimations();
    initAnnouncementDismiss();
    initStickyHeader();
    initNewsletterForm();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// Product recommendation carousel arrows
(function(){
  document.addEventListener('click', function(event){
    const prev = event.target.closest('[data-carousel-prev]');
    const next = event.target.closest('[data-carousel-next]');
    if (!prev && !next) return;
    const section = event.target.closest('[data-featured-products-carousel]');
    if (!section) return;
    const track = section.querySelector('[data-carousel-track]');
    if (!track) return;
    const card = track.querySelector('.product-card');
    const step = card ? card.getBoundingClientRect().width + 28 : Math.round(track.clientWidth * 0.8);
    track.scrollBy({ left: next ? step : -step, behavior: 'smooth' });
  });
})();


// SMART-O final ads auto-rotate
(function(){
  const rotators = document.querySelectorAll('[data-final-ads-rotator]');
  if (!rotators.length) return;

  rotators.forEach(function(rotator){
    const track = rotator.querySelector('[data-carousel-track]');
    const slides = track ? Array.from(track.children) : [];
    if (!track || slides.length < 2) return;

    const dotsWrap = rotator.querySelector('[data-final-ads-dots]');
    const dots = dotsWrap ? Array.from(dotsWrap.querySelectorAll('button')) : [];
    let index = 0;
    let timer = null;

    function updateDots(){
      dots.forEach(function(dot, dotIndex){
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
    }

    function goTo(nextIndex){
      index = (nextIndex + slides.length) % slides.length;
      const target = slides[index];
      if (!target) return;
      track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      updateDots();
    }

    function start(){
      stop();
      timer = window.setInterval(function(){
        goTo(index + 1);
      }, 6500);
    }

    function stop(){
      if (timer){
        window.clearInterval(timer);
        timer = null;
      }
    }

    const prev = rotator.querySelector('[data-final-ads-prev]');
    const next = rotator.querySelector('[data-final-ads-next]');
    if (prev){
      prev.addEventListener('click', function(){
        goTo(index - 1);
        start();
      });
    }
    if (next){
      next.addEventListener('click', function(){
        goTo(index + 1);
        start();
      });
    }

    dots.forEach(function(dot, dotIndex){
      dot.addEventListener('click', function(){
        goTo(dotIndex);
        start();
      });
    });

    track.addEventListener('scroll', function(){
      const left = track.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach(function(slide, slideIndex){
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < closestDistance){
          closestDistance = distance;
          closestIndex = slideIndex;
        }
      });
      index = closestIndex;
      updateDots();
    }, { passive: true });

    updateDots();
    rotator.addEventListener('mouseenter', stop);
    rotator.addEventListener('mouseleave', start);
    rotator.addEventListener('focusin', stop);
    rotator.addEventListener('focusout', function(event){
      if (!rotator.contains(event.relatedTarget)) start();
    });

    start();
  });
})();
